import React, { useCallback, useState } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { CorporateTheme, CreateFormTheme } from "../../utils/themes";
import StyledText from "../../components/StyledComponents/StyledText";
import { FieldArray, Form, Formik, Field } from "formik";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import * as Yup from "yup";
import FormikSelect from "../../components/Formik/FormikSelect";

import { useNavigate } from "react-router-dom";
import DocumentIcon from "../../assets/icons/document-text";
import FormikInputField from "../../components/Formik/FormikInputField";
import CustomSelect from "../../components/form/CustomSelect";
import CustomInput from "../../components/form/CustomInput";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import AddIcon from "../../assets/icons/AddIcon";
import NewContactIcon from "../../assets/icons/NewContactIcon";
import { useGetContactRolesQuery, useGetCriteriaConditionsQuery, useLazyGetContactRolesQuery } from "../../api/searchQueryApiSlice";
import { formatLabel } from "../../utils/helper";
import FormikMultiSelect from "../../components/Formik/FormikMultiSelect";
import FormikNumberRangeField from "../../components/Formik/FormikNumberRange";
import FormikCheckBox from "../../components/Formik/FormikCheckBox";
import { SkeletonCell } from "../../components/SkeletonCell";
import { useTheme } from "../../middleware/ThemeContext";
import SaveIcon from "../../assets/icons/SaveIcon";
import AMSSelect from "../../components/AMSSelect";
import { usePaginatedQuery } from "../../hooks/usePaginatedOptions";
import { useLazyGetOrganizationsMinimalQuery } from "../../api/organizationApiSlice";
import ModalHeaderBg from "../../assets/images/CreateFormBackground.png"
import CloseIcon from "../../assets/icons/CloseIcon";
import SimpleBar from "simplebar-react";
import CalendarIcon from "../../assets/icons/CalendarIcon";
import { getIconForDataType } from "../../components/FormBuilder/FieldSettings/fieldSettingsHelper";
interface CriteriaModalProps {
  onClose: () => void;
  title?: string;
  subTitle?: string;
  edit?: boolean;
  columns?: any[]; // optional, if you want to pass dynamic columns
  setSelectedCriteria?: (criteria: any) => void; // function to set selected criteria
  selectedCriteria?: any; // optional, if you want to pass selected criteria
}

type Column = {
  field: string;
  label: string;
  dataType: string;
  [key: string]: any;
};

type Condition = {
  name: string;
  friendlyName: string;
  description: string;
  valueType: string;
  expectedValueType: string[];
};

type CriteriaValue =
  | string
  | number
  | string[]
  | boolean
  | {
    start?: string | null;
    end?: string | null;
  }
  | null;

interface ConditionValues {
  id: number;
  column: string;
  condition: string;
  value: CriteriaValue;
}

interface ConditionGroupValues {
  id: number;
  isGroup: true;
  logic: "All" | "Any";
  conditions: ConditionValues[];
}

type ConditionItem = ConditionValues | ConditionGroupValues;

interface ConditionType {
  conditions: ConditionItem[];
}

const generateCriteriaId = () => new Date().getTime() + Math.random();

const createEmptyCondition = (): ConditionValues => ({
  id: generateCriteriaId(),
  column: "",
  condition: "",
  value: "",
});

const createEmptyGroup = (): ConditionGroupValues => ({
  id: generateCriteriaId(),
  isGroup: true,
  logic: "All",
  conditions: [createEmptyCondition()],
});

const resolveLogicValue = (value?: any): "All" | "Any" => {
  if (!value) return "All";
  if (typeof value === "object") {
    const raw = value?.value ?? value?.label;
    if (raw === "Any" || raw === "OR") return "Any";
    if (raw === "All" || raw === "AND") return "All";
  }
  if (value === "Any" || value === "OR") return "Any";
  return "All";
};



const CriteriaModal: React.FC<CriteriaModalProps> = ({
  onClose,
  title,
  subTitle,
  edit,
  columns,
  setSelectedCriteria,
  selectedCriteria,
}) => {
  const { theme } = useTheme();
  const [logic, setLogic] = useState(resolveLogicValue(selectedCriteria?.logic));

  const { data: conditionOptions, isLoading } = useGetCriteriaConditionsQuery();
  // const { data: contactRoles } = useGetContactRolesQuery({ page: 1, perPage: 50 });
  const {
    items: roles,
    isLoading: rolesLoading,
    hasMore,
    fetchNextPage,
  } = usePaginatedQuery({
    lazyHook: useLazyGetContactRolesQuery,
    selectItems: (data) => data?.roles || [],
    perPage: 10,
  });

  const {
    items: organizations,
    isLoading: organizationsLoading,
    hasMore: organizationsHasMore,
    fetchNextPage: fetchOrganizationsNextPage,
  } = usePaginatedQuery({
    lazyHook: useLazyGetOrganizationsMinimalQuery,
    selectItems: (data) => data?.organizations || [],
    perPage: 10,
  });

  const normalizeConditionValue = (item: any): ConditionValues => {
    if (!item) {
      return createEmptyCondition();
    }

    const column = item.column ?? item.field ?? "";
    const condition = item.condition ?? item.operator ?? "";
    let val = item.value ?? "";

    if (Array.isArray(val) && val.length === 2 && condition === "between") {
      val = { start: val[0], end: val[1] };
    }

    return {
      id: item.id ?? generateCriteriaId(),
      column,
      condition,
      value: val,
    };
  };

  const normalizeCriteriaItems = (criteriaList?: any[]): ConditionItem[] => {
    if (!Array.isArray(criteriaList)) {
      return [createEmptyCondition()];
    }

    if (criteriaList.length === 0) {
      return [];
    }

    const mapped = criteriaList.map((item: any) => {
      const isGroupLike = item?.isGroup || (item?.logic && Array.isArray(item?.conditions) && !item?.column && !item?.field);

      if (isGroupLike) {
        const children = (item?.conditions || [])
          .map((child: any) => normalizeConditionValue(child))
          .filter((child: ConditionValues) => typeof child.column === "string");

        return {
          id: item?.id ?? generateCriteriaId(),
          isGroup: true,
          logic: resolveLogicValue(item?.logic),
          conditions: children.length ? children : [createEmptyCondition()],
        } as ConditionGroupValues;
      }

      return normalizeConditionValue(item);
    });

    return mapped.length ? mapped : [];
  };

  const CriteriaInitialValues = {
    conditions: normalizeCriteriaItems(selectedCriteria?.criteria),
  };

  const conditionSchema = Yup.object().shape({
    id: Yup.mixed().required(),
    column: Yup.string().required("Column is required"),
    condition: Yup.string().required("Condition is required"),
    value: Yup.mixed().when(
      "condition",
      (conditionValues: any[], schema: any) => {
        const conditionVal = Array.isArray(conditionValues) ? conditionValues[0] : conditionValues;
        const selectedCondition = (conditionOptions ?? []).find(
          (cond: Condition) => cond.name === conditionVal
        );

        if (
          !conditionVal ||
          conditionVal === "isNull" ||
          conditionVal === "isNotNull" ||
          selectedCondition?.valueType === "none"
        ) {
          return Yup.mixed().notRequired();
        }

        return Yup.lazy((val: any) => {
          if (typeof val === "string") {
            return Yup.string().required("Required");
          }

          if (typeof val === "number") {
            return Yup.number().required("Required");
          }

          if (
            val &&
            typeof val === "object" &&
            "start" in val &&
            "end" in val
          ) {
            return Yup.object().shape({
              start: Yup.date()
                .nullable()
                .required("Start date Required"),
              end: Yup.date().nullable().required("End date Required"),
            });
          }

          if (Array.isArray(val)) {
            if (
              val.length === 2 &&
              val.every((v) => typeof v === "number")
            ) {
              return Yup.array()
                .of(Yup.number())
                .length(2, "Enter two numbers for range")
                .required("Number range is required");
            }

            return Yup.array()
              .of(Yup.mixed())
              .min(1, "Select at least one option")
              .required("Select is required");
          }

          if (typeof val === "boolean") {
            return Yup.boolean().required("Value is required");
          }

          return Yup.mixed().required("Required");
        });
      }
    ),
  });

  const groupSchema = Yup.object().shape({
    id: Yup.mixed().required(),
    isGroup: Yup.boolean().oneOf([true]).required(),
    logic: Yup.string().oneOf(["All", "Any"]).required("Logic is required"),
    conditions: Yup.array()
      .min(1, "Add at least one condition")
      .of(conditionSchema),
  });

  const criteriaSchema = Yup.object().shape({
    conditions: Yup.array()
      .of(
        Yup.lazy((value) => (value?.isGroup ? groupSchema : conditionSchema))
      )
      .default([]),
  });

  const transformConditionForSubmit = (item: ConditionValues): ConditionValues => {
    const matchedCondition = (conditionOptions ?? []).find(
      (cond: Condition) => cond.name === item.condition
    );

    let val = item.value;

    if (matchedCondition?.valueType === "none") {
      return { ...item, value: null };
    }

    if (val && typeof val === "object" && "start" in val && "end" in val) {
      val = [(val as any).start, (val as any).end];
    } else if (typeof val === "boolean") {
      val = val.toString();
    }

    return { ...item, value: val };
  };

  const prepareCriteriaForSubmit = (items: ConditionItem[]): ConditionItem[] =>
    items.map((item) => {
      if ((item as ConditionGroupValues)?.isGroup) {
        const group = item as ConditionGroupValues;
        return {
          ...group,
          conditions: group.conditions.map(transformConditionForSubmit),
        };
      }
      return transformConditionForSubmit(item as ConditionValues);
    });

  const handleSubmit = (values: ConditionType) => {
    const converted = prepareCriteriaForSubmit(values?.conditions ?? []);

    if (setSelectedCriteria) {
      setSelectedCriteria({
        criteria: converted,
        logic: { label: logic, value: logic },
      });
      onClose();
    }
  };

  function getValidConditionsForColumn(column: any, operators: any) {
    if (!column?.dataType) return [];

    const baseOps = (() => {
      switch (column.dataType) {
        case "string":
          return operators.filter(
            (op: any) =>
              op.valueType === "single" &&
              op.expectedValueType.includes("string")
          );
        case "number":
          return operators.filter(
            (op: any) =>
              ["single", "array (length = 2)"].includes(op.valueType) &&
              op.expectedValueType.includes("number")
          );
        case "date":
          return operators.filter(
            (op: any) =>
              // ["single", "array (length = 2)"].includes(op.valueType) &&
              op.expectedValueType.includes("date")
          );
        case "boolean":
          return operators.filter(
            (op: any) =>
              op.valueType === "single" &&
              op.expectedValueType.includes("boolean")
          );
        case "select":
          return operators.filter(
            (op: any) =>
              op.valueType === "array" &&
              (op.expectedValueType.includes("string") ||
                op.expectedValueType.includes("number"))
          );
        case "multi-select":
          return operators.filter(
            (op: any) =>
              op.valueType === "array" &&
              (op.expectedValueType.includes("string") ||
                op.expectedValueType.includes("number"))
          );
        default:
          return [];
      }
    })();

    // Always include isNull and isNotNull
    const nullOps = operators.filter(
      (op: any) => op.name === "isNull" || op.name === "isNotNull"
    );

    // Combine and deduplicate (just in case)
    const combined = [...baseOps, ...nullOps];
    const unique = Array.from(
      new Map(combined.map((op) => [op.name, op])).values()
    );

    return unique;
  }

  function getValueFieldType(
    column?: Column,
    condition?: Condition
  ):
    | "text"
    | "select"
    | "date"
    | "dateRange"
    | "number"
    | "numberRange"
    | "checkBox"
    | "none" | "multi-select" {
    if (!column || !condition) return "none";

    const { dataType } = column;
    const { valueType, expectedValueType } = condition;

    const includes = (types: string[]) =>
      types.some((t) => expectedValueType.includes(t));

    // Case 1: Text input
    if (
      valueType === "single" &&
      dataType === "string" &&
      includes(["string", "number"])
    ) {
      return "text";
    }

    if (
      valueType === "single" &&
      dataType === "number" &&
      includes(["number"])
    ) {
      return "number";
    }

    if (
      valueType === "array (length = 2)" &&
      dataType === "number" &&
      includes(["number"])
    ) {
      return "numberRange";
    }

    // Case 2: Select input (from column.options)
    if (
      (valueType === "array" || valueType === "single") &&
      (dataType === "select" || dataType === "multi-select") &&
      includes(["string", "number"])
    ) {
      return "select";
    }

    // Case 3: Date input
    if (
      (valueType === "array" || valueType === "single") &&
      dataType === "date" &&
      includes(["string", "number", "date"])
    ) {
      return "date";
    }

    if (
      valueType === "array (length = 2)" &&
      dataType === "date" &&
      includes(["number", "date"])
    ) {
      return "dateRange";
    }

    if (
      valueType === "single" &&
      dataType === "boolean" &&
      includes(["boolean"])
    ) {
      return "checkBox";
    }

    return "none"; // fallback
  }


  return (
    <Modal show={true} onHide={onClose} size="xl" centered>
      <StyledModalContent theme={theme}>
        <ModalHeader>
          <ModalCloseButton onClick={onClose}><CloseIcon color={theme.CorporateTheme?.colors?.textSecondary} /></ModalCloseButton>
          <ModalTitle>
            <TitleText theme={theme}>{title ?? "Edit Query Criteria"}</TitleText>
            <StyledText
              fontSize="14px"
              fontWeight={500}
              textColor={theme.CorporateTheme?.colors?.textSecondary}
              lineHeight="1.7"
              className="pb-8"
            >
              {subTitle ??
                <>
                  Use the options below to modify your query criteria and get results that
                  <br />
                  better align with your goals
                </>}
            </StyledText>
          </ModalTitle>
        </ModalHeader>
        <Formik
          initialValues={CriteriaInitialValues}
          // enableReinitialize={true}s
          validationSchema={criteriaSchema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, setFieldValue, values, touched, errors }) => (
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                <ContentCotainer theme={theme} className="d-flex flex-column mx-5">

                  <FieldArray name="conditions">
                    {(arrayHelpers) => {
                      const setLogicValue = (val: any, targetPath: string) => {
                        const resolved = resolveLogicValue(val);
                        setFieldValue(targetPath, resolved);
                      };

                      const renderConditionRow = (
                        item: ConditionValues,
                        namePrefix: string,
                        onRemove: () => void,
                        isNested = false
                      ) => {
                        const selectedField = item.column;
                        const selectedColumn = (columns ?? [])?.find(
                          (col) => col.field === selectedField
                        );
                        const validConditions = getValidConditionsForColumn(
                          selectedColumn,
                          conditionOptions ?? []
                        );
                        const selectedCondition = (conditionOptions ?? []).find(
                          (cond: any) => cond.name === item.condition
                        );
                        const valueFieldType = getValueFieldType(
                          selectedColumn,
                          selectedCondition
                        );

                        return (
                          <div
                            key={item.id}
                            className={`d-grid ${valueFieldType === "none" && "custom-grid"}`}
                            style={{
                              gridTemplateColumns: "1fr 1fr 1fr auto",
                              gap: "0.5rem",
                              alignItems: "start",
                              marginTop: isNested ? "0.35rem" : "0.5rem",
                            }}
                          >
                            <div>
                              <AMSSelect
                                staticOptions={columns?.map((col) => ({
                                  label: col?.label,
                                  value: col?.field,
                                  dataType: col?.dataType,
                                  accessKey: col?.field,
                                  id: col?.field,
                                  icon: getIconForDataType(col?.dataType)
                                })) ?? []}
                                value={(() => {
                                  const opts = (columns?.map((col) => ({
                                    label: col?.label,
                                    value: col?.field,
                                    dataType: col?.dataType,
                                    accessKey: col?.field,
                                    id: col?.field,
                                  })) ?? []);
                                  return opts.find((opt: any) => String(opt.value) === String(item.column)) ?? null;
                                })()}
                                onChange={(opt: any) => {
                                  const nextValue = opt?.value ? String(opt.value) : "";
                                  setFieldValue(`${namePrefix}.column`, nextValue);
                                  setFieldValue(`${namePrefix}.condition`, "");
                                  setFieldValue(`${namePrefix}.value`, "");
                                }}
                                placeholder="Select column here"
                                isSearchable
                                showDataType
                                includeAccessKey
                                menuPosition="fixed"
                              />
                            </div>

                            <div>
                              <FormikSelect
                                value={(conditionOptions ?? []).find((opt: any) => opt.name === item.condition)?.friendlyName}
                                name={`${namePrefix}.condition`}
                                options={validConditions?.map((cond: any) => ({
                                  label: cond.friendlyName,
                                  value: cond.name,
                                })) ?? []}
                                placeholder="Select condition here"
                                onChange={(val) => {
                                  const nextVal = typeof val === "string" ? val : val?.value;
                                  setFieldValue(`${namePrefix}.condition`, nextVal ?? "");
                                  setFieldValue(`${namePrefix}.value`, "");
                                }}
                              />
                            </div>

                            {valueFieldType !== "none" && valueFieldType !== "checkBox" ? (
                              <div>
                                {["text", "number", "select", "multi-select"].includes(valueFieldType) ? (
                                  <div className="flex-fill">
                                    {valueFieldType === "text" && (
                                      <FormikInputField
                                        type="text"
                                        name={`${namePrefix}.value`}
                                        placeholder={`Enter ${formatLabel(selectedColumn?.field)}`}
                                        className="form-control w-100"
                                      />
                                    )}
                                    {valueFieldType === "number" && (
                                      <FormikInputField
                                        type="number"
                                        min={0}
                                        name={`${namePrefix}.value`}
                                        placeholder={`Enter ${formatLabel(selectedColumn?.field)}`}
                                        className="form-control w-100"
                                      />
                                    )}
                                    {valueFieldType === "select" && (
                                      selectedColumn?.dataType === "multi-select" && selectedColumn?.field === "r.name" ? (
                                        <FormikMultiSelect
                                          count
                                          buttonStyle={{ width: "100%" }}
                                          name={`${namePrefix}.value`}
                                          options={roles?.map((opt: any) => ({
                                            id: opt.id,
                                            value: opt.name,
                                            name: opt.name,
                                          }))}
                                          placeholder="Select one or more"
                                          isLoading={rolesLoading}
                                          hasMore={hasMore}
                                          onFetchMore={fetchNextPage}
                                          className="w-100"
                                        />
                                      ) : selectedColumn?.dataType === "multi-select" && selectedColumn?.field === "o.name" ? (
                                        <FormikMultiSelect
                                          count
                                          buttonStyle={{ width: "100%" }}
                                          name={`${namePrefix}.value`}
                                          options={organizations?.map((opt: any) => ({
                                            id: opt.id,
                                            value: opt.name,
                                            name: opt.name,
                                          }))}
                                          placeholder="Select one or more"
                                          isLoading={organizationsLoading}
                                          hasMore={organizationsHasMore}
                                          onFetchMore={fetchOrganizationsNextPage}
                                          className="w-100"
                                        />
                                      ) : (
                                        <FormikMultiSelect
                                          count
                                          buttonStyle={{ width: "100%" }}
                                          name={`${namePrefix}.value`}
                                          options={
                                            selectedColumn?.options?.map((opt: any) => ({
                                              id: opt?.label,
                                              value: opt?.label,
                                              name: opt?.label,
                                            })) || []
                                          }
                                          placeholder="Select one or more"
                                          className="w-100"
                                        />
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <div className="d-flex align-items-center">
                                    {valueFieldType === "numberRange" && (
                                      <FormikNumberRangeField name={`${namePrefix}.value`} className="w-100" />
                                    )}
                                    {valueFieldType === "date" && (
                                      <Field name={`${namePrefix}.value`}>
                                        {({ field, form }: any) => (
                                          <CustomInput
                                            type="date"
                                            value={field.value || ''}
                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                            onBlur={field.onBlur}
                                            rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                            placeholder="Choose a date"
                                          />
                                        )}
                                      </Field>
                                    )}
                                    {valueFieldType === "dateRange" && (
                                      <Field name={`${namePrefix}.value`}>
                                        {({ field, form }: any) => (
                                          <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                                            <CustomInput
                                              type="date"
                                              value={field.value?.start || field.value?.[0] || ''}
                                              onChange={(value) => form.setFieldValue(field.name, { ...field.value, start: value })}
                                              rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                              placeholder="Start date"
                                              style={{ flex: 1 }}
                                            />
                                            <CustomInput
                                              type="date"
                                              value={field.value?.end || field.value?.[1] || ''}
                                              onChange={(value) => form.setFieldValue(field.name, { ...field.value, end: value })}
                                              rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                                              placeholder="End date"
                                              style={{ flex: 1 }}
                                            />
                                          </div>
                                        )}
                                      </Field>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : null}

                            {valueFieldType !== "none" && valueFieldType === "checkBox" ? (
                              <div className="mt-2 pt-1">
                                <FormikCheckBox
                                  name={`${namePrefix}.value`}
                                  label={`${formatLabel(selectedColumn?.label)}`}
                                  size={18}
                                  borderColor={theme.CorporateTheme?.colors?.primary}
                                  bgColor={theme.CorporateTheme?.colors?.primary}
                                  checkColor="white"
                                  className="w-100"
                                />
                              </div>
                            ) : null}

                            <div className="d-flex mt-1 pt-1">
                              <DeleteIcon
                                color={theme.CorporateTheme.colors.textSecondary}
                                size={22}
                                className="cursor-pointer"
                                onClick={onRemove}
                              />
                            </div>
                          </div>
                        );
                      };

                      const renderGroup = (
                        group: ConditionGroupValues,
                        index: number
                      ) => {
                        const groupName = `conditions.${index}`;
                        return (
                          <NestedLogicGroup key={group.id} theme={theme}>
                            <div className="d-flex justify-content-center">
                              <StyledText
                                fontSize="14px"
                                fontWeight={600}
                                textColor={theme.CorporateTheme?.colors?.headerColor}
                              >
                                Nested Logic Group
                              </StyledText>
                            </div>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                              <StyledText
                                fontSize="14px"
                                fontWeight={600}
                                textColor={theme.CorporateTheme?.colors?.headerColor}
                              >
                                Apply criteria to fields
                              </StyledText>
                              <DeleteIcon
                                color={theme.CorporateTheme.colors.textSecondary}
                                size={22}
                                className="cursor-pointer"
                                onClick={() => arrayHelpers.remove(index)}
                              />
                            </div>
                            <CustomSelect
                              options={["All", "Any"]}
                              value={group.logic}
                              onChange={(val: any) => setLogicValue(val, `${groupName}.logic`)}
                              placeholder=" "
                              className="mb-2"
                            />
                            <FieldArray name={`${groupName}.conditions`}>
                              {(groupHelpers) => (
                                <>
                                  {group?.conditions?.map((child, childIndex) =>
                                    renderConditionRow(
                                      child,
                                      `${groupName}.conditions.${childIndex}`,
                                      () => groupHelpers.remove(childIndex),
                                      true
                                    )
                                  )}
                                  <div className="d-flex justify-content-center">
                                    <Button
                                      theme={theme}
                                      type="button"
                                      className="cursor-pointer mt-3"
                                      $variant="cancel"
                                      onClick={() => groupHelpers.push(createEmptyCondition())}
                                    >
                                      <AddIcon color={theme.CorporateTheme.colors.textSecondary} />
                                      Add Condition
                                    </Button>
                                  </div>
                                </>
                              )}
                            </FieldArray>
                          </NestedLogicGroup>
                        );
                      };

                      return (
                        <>
                          <div className="mb-3">
                            <StyledText
                              className="d-flex align-items-center gap-2  mb-2"
                              fontSize="14px"
                              fontWeight={600}
                              textColor={theme.CreateFormTheme?.colors?.headerColor}
                            >
                              Apply criteria to fields
                            </StyledText>
                            <CustomSelect
                              options={["All", "Any"]}
                              value={logic}
                              onChange={(val: any) => setLogic(resolveLogicValue(val))}
                              placeholder=" "
                            />
                          </div>



                          <StyledText
                            className="d-flex align-items-center gap-2 "
                            fontSize="14px"
                            fontWeight={600}
                            textColor={theme.CreateFormTheme?.colors?.headerColor}
                          >
                            Available custom fields
                          </StyledText>

                          {isLoading ? (
                            <div className="d-flex align-items-center gap-1 px-2 mt-4 ">
                              {Array(3)
                                .fill(null)
                                .map((_, i) => (
                                  <SkeletonCell
                                    key={i}
                                    style={{
                                      maxWidth: "100%",
                                      height: "36px",
                                      marginBottom: "10px",
                                    }}
                                  />
                                ))}
                            </div>
                          ) : (
                            <><div className="py-0 w-100 ">
                              <div className="d-flex flex-column">
                                {values?.conditions?.map((item: ConditionItem, index: number) => (item as ConditionGroupValues)?.isGroup
                                  ? renderGroup(item as ConditionGroupValues, index)
                                  : renderConditionRow(
                                    item as ConditionValues,
                                    `conditions.${index}`,
                                    () => arrayHelpers.remove(index)
                                  )
                                )}
                              </div>
                            </div>
                              <div className="d-flex justify-content-center m-4">
                                <Button
                                  theme={theme}
                                  type="button"
                                  className="cursor-pointer m-2"
                                  $variant="cancel"
                                  onClick={() => arrayHelpers.push(createEmptyCondition())}
                                >
                                  <AddIcon color={theme.CorporateTheme.colors.textSecondary} />
                                  Add Condition
                                </Button>
                                <Button
                                  theme={theme}
                                  type="button"
                                  className="cursor-pointer m-2"
                                  $variant="cancel"
                                  onClick={() => arrayHelpers.push(createEmptyGroup())}
                                >
                                  <AddIcon color={theme.CorporateTheme.colors.textSecondary} />
                                  Add Logic Group
                                </Button>
                              </div></>
                          )}
                        </>
                      );
                    }}
                  </FieldArray>
                </ContentCotainer>
                <div className="d-flex align-items-center justify-content-end mt-3 me-5">
                  <div className="d-flex gap-1">
                    <Button
                      theme={theme}
                      type="button"
                      className={`cursor-pointer ${
                        //   updateFolderLoading || addFolderLoading
                        // ? "disabled"
                        // :
                        ""
                        }`}
                      $variant="cancel"
                      onClick={onClose}
                    >
                      <CrossIcon />
                      Cancel
                    </Button>
                    <Button
                      theme={theme}
                      type="submit"
                      $variant="add"
                      className="cursor-ponter"
                    >
                      <div className="d-flex align-items-center gap-1 cursor-pointer">
                        <label className="cursor-pointer">
                          <SaveIcon color={theme.CorporateTheme.colors.background} /> Save{" "}
                        </label>
                      </div>
                      {/* )} */}
                    </Button>
                  </div>
                </div>
              </ModalBody>
            </Form>
          )}
        </Formik>

        <ModalFooter></ModalFooter>
      </StyledModalContent>
    </Modal>
  );
};

export default CriteriaModal;

const StyledModalContent = styled.div`
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  background: ${({ theme }) => theme.colors.contentBackground};
`;

const ModalHeader = styled.div`
  position: relative;
  // padding-bottom: 30px;
`;

const ModalTitle = styled.div`
  text-align: center;
  padding: 30px 30px;
  margin-top: 30px;
  background: url(${ModalHeaderBg}) no-repeat center/cover;
  background-position: center 6%;
  background-size: 70% auto;
`;

const ModalBody = styled.div`
  padding-top: 0;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  // bottom: 50px;
  top: -25px;
  right: 10px;
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    // background: #f5f5f5;
    color: #333;
    padding: 0px;
  }
`;

const TitleText = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.CorporateTheme?.colors.primary};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  // padding-top: 20px;
  padding: 30px;
`;

const NestedLogicGroup = styled.div`
  border: 1px dashed ${({ theme }) => theme?.CorporateTheme?.colors?.border};
  border-radius: 6px;
  padding: 16px;
  margin-top: 8px;
  // margin-left: 20px;
  background: ${({ theme }) => theme?.CorporateTheme?.colors?.filterBG};
`;


const ContentCotainer = styled.div`
  padding: 30px;
  border: 1px solid ${({ theme }) => theme?.CreateFormTheme?.colors?.border};
  border-radius: 5px;
  margin-bottom: 35px;
`;